import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { fiscalSponsorshipDocumentaristsContent } from '../content/educational/fiscalSponsorshipDocumentaristsContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 40;
    const footerHeight = 20;
    const lineSpacing = 3;
    const paragraphSpacing = 1.2;
    const bulletIndent = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let processedText = text;
      let segments = [];
      
      let lastIndex = 0;
      let boldRegex = /\*\*(.*?)\*\*/g;
      let match;
      
      while ((match = boldRegex.exec(processedText)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: processedText.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < processedText.length) {
        segments.push({
          text: processedText.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Helper function to handle bold keywords
    const processKeywords = (text, keywords) => {
      let processedText = text;
      keywords.forEach(keyword => {
        const regex = new RegExp(`(${keyword})`, 'gi');
        processedText = processedText.replace(regex, '**$1**');
      });
      return processedText;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n').filter(line => line.trim() !== '');
      let currentY = yPosition;
      
      lines.forEach(line => {
        if (line.trim() === '') return;
        
        const trimmedLine = line.trim();
        
        if (trimmedLine.startsWith('-')) {
          const bulletText = trimmedLine.substring(1).trim();
          
          if (bulletText) {
            // Add bullet point
            doc.text('•', margin + bulletIndent, currentY);
            const x = margin + bulletIndent + 8;
            
            // Split on colon if it exists
            const colonIndex = bulletText.indexOf(':');
            if (colonIndex !== -1) {
              // Get the parts before and after the colon
              const beforeColon = bulletText.substring(0, colonIndex).replace(/\*\*/g, '').trim();
              const afterColon = bulletText.substring(colonIndex + 1).replace(/\*\*/g, '').trim();
              
              // Render the part before colon in bold
              doc.setFont('helvetica', 'bold');
              doc.text(beforeColon + ':', x, currentY);
              const boldWidth = doc.getTextWidth(beforeColon + ': ');
              
              // Render the part after colon in normal weight
              doc.setFont('helvetica', 'normal');
              const availableWidth = contentWidth - (bulletIndent + 20) - boldWidth;
              const restLines = doc.splitTextToSize(afterColon, availableWidth);
              
              if (restLines.length > 1) {
                // Multi-line text
                restLines.forEach((line, index) => {
                  doc.text(line, x + boldWidth, currentY + (index * 4));
                });
                currentY += (restLines.length - 1) * 4;
              } else {
                // Single line text
                doc.text(afterColon, x + boldWidth, currentY);
              }
            } else {
              // No colon - render normally
              doc.setFont('helvetica', 'normal');
              const lines = doc.splitTextToSize(bulletText, contentWidth - (bulletIndent + 20));
              doc.text(lines, x, currentY);
              if (lines.length > 1) {
                currentY += (lines.length - 1) * 4;
              }
            }
            
            currentY += 6; // Space between bullet points
          }
        } else {
          // Regular paragraph text
          doc.setFont('helvetica', 'normal');
          const lines = doc.splitTextToSize(trimmedLine, contentWidth);
          doc.text(lines, margin, currentY);
          currentY += lines.length * 4;
        }
      });
      
      return currentY;
    };

    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with optimized styling
      doc.setFontSize(22);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += (titleLines.length * 7) + 12;

      content.forEach((section, index) => {
        // Check if there's enough space for the section heading and at least 2 lines
        if (yPosition + 50 > pageHeight - footerHeight - 45) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10; // Increased spacing after heading

        // Process section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          // Check remaining space before processing each paragraph
          if (yPosition + 20 > pageHeight - footerHeight - 45) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing * 2; // Increased spacing between paragraphs
        });

        // Add extra space between sections
        yPosition += 8;

        // Add CTA only if we have enough space
        if (index === content.length - 1) {
          if (yPosition + 60 > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 12;
          }
          addCallToAction(pageHeight - footerHeight - 50);
        }
      });
    };

    // Add this function near the other helper functions
    const renderBulletText = (text, x, y, maxWidth) => {
      // First handle markdown-style bold text
      const processMarkdownBold = (text) => {
        const segments = [];
        const boldRegex = /\*\*(.*?)\*\*/g;
        let lastIndex = 0;
        let match;

        while ((match = boldRegex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            segments.push({
              text: text.substring(lastIndex, match.index),
              bold: false
            });
          }
          segments.push({
            text: match[1],
            bold: true
          });
          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
          segments.push({
            text: text.substring(lastIndex),
            bold: false
          });
        }

        return segments;
      };

      // Split the text into parts: description and detail (after ":")
      const parts = text.split(':');
      
      if (parts.length > 1) {
        // If there's a colon, handle the parts separately
        const description = parts[0].trim() + ':';
        const detail = parts[1].trim();
        
        // Process the description part for markdown bold
        const descriptionSegments = processMarkdownBold(description);
        let currentX = x;
        
        // Render description segments
        descriptionSegments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        
        // Calculate width used by description
        const descWidth = currentX - x;
        
        // Process and render detail part
        const detailSegments = processMarkdownBold(detail);
        const detailText = detail.replace(/\*\*/g, ''); // Remove markdown for width calculation
        const detailLines = doc.splitTextToSize(detailText, maxWidth - descWidth);
        
        if (detailLines.length > 1) {
          // If detail wraps, put it on next line
          let detailY = y + 4;
          detailSegments.forEach(segment => {
            doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
            const lines = doc.splitTextToSize(segment.text, maxWidth);
            doc.text(lines, x, detailY);
            detailY += lines.length * 4;
          });
          return (detailLines.length + 1) * 4;
        } else {
          // If single line, render on same line
          currentX = x + descWidth;
          detailSegments.forEach(segment => {
            doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
            doc.text(segment.text, currentX, y);
            currentX += doc.getTextWidth(segment.text);
          });
          return 4;
        }
      } else {
        // If no colon, process entire text for markdown bold
        const segments = processMarkdownBold(text);
        let currentX = x;
        const lines = doc.splitTextToSize(text.replace(/\*\*/g, ''), maxWidth);
        
        segments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        
        return lines.length * 4;
      }
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Documentary Filmmaking Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact'
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateDocumentaristsOverview = async () => {
  try {
    const doc = await generatePDF(
      fiscalSponsorshipDocumentaristsContent.sections,
      fiscalSponsorshipDocumentaristsContent.title,
      fiscalSponsorshipDocumentaristsContent.keywords
    );
    
    if (!doc) {
      throw new Error('PDF generation failed');
    }
    
    return doc; // Return the doc object for ResourceManager to handle
  } catch (error) {
    console.error('Error in generateDocumentaristsOverview:', error);
    return null;
  }
}; 